// Monitoring state and API coordination

import { Request } from "@asanrom/request-browser";
import { ApiDashboard } from "@/api/api-group-dashboard";
import { ApiChecks } from "@/api/api-group-checks";
import type { CheckExecutionRealtimeEvent, CheckResultStatus, MonitoringRealtimeEvent } from "@/types/monitoring";
import { ApiProjects } from "@/api/api-group-projects";
import { ApiServices } from "@/api/api-group-services";
import type {
    CheckCreateRequest,
    CheckResultListResponse,
    CheckResultResponse,
    CheckResponse,
    CheckRunResponse,
    CheckUpdateRequest,
    DashboardCheckResponse,
    DashboardProjectResponse,
    DashboardResponse,
    DashboardServiceResponse,
    IncidentResponse,
    ProjectCreateRequest,
    ProjectResponse,
    ProjectUpdateRequest,
    ServiceCreateRequest,
    ServiceResponse,
    ServiceUpdateRequest,
} from "@/api/definitions";
import { Timeouts } from "@/utils/timeout";
import { AppEvents } from "./app-events";
import { AuthController } from "./auth";

const DASHBOARD_REQUEST_ID = "monitoring-controller-dashboard";
const PROJECT_CREATE_REQUEST_ID = "monitoring-controller-project-create";
const PROJECT_UPDATE_REQUEST_ID = "monitoring-controller-project-update";
const PROJECT_CERTIFICATE_CHECK_REQUEST_ID = "monitoring-controller-project-certificate-check";
const SERVICE_CREATE_REQUEST_ID = "monitoring-controller-service-create";
const SERVICE_UPDATE_REQUEST_ID = "monitoring-controller-service-update";
const SERVICE_DELETE_REQUEST_ID = "monitoring-controller-service-delete";
const CHECK_DETAIL_REQUEST_ID = "monitoring-controller-check-detail";
const CHECK_RESULTS_REQUEST_ID = "monitoring-controller-check-results";
const CHECK_LATEST_RESULT_REQUEST_ID = "monitoring-controller-check-latest-result";
const CHECK_RUN_REQUEST_ID = "monitoring-controller-check-run";
const CHECK_CREATE_REQUEST_ID = "monitoring-controller-check-create";
const CHECK_UPDATE_REQUEST_ID = "monitoring-controller-check-update";
const CHECK_DELETE_REQUEST_ID = "monitoring-controller-check-delete";

export type MonitoringErrorKind = "bad-request" | "not-found" | "unauthorized" | "temporary" | "server" | "network" | "unexpected";

export interface MonitoringError {
    kind: MonitoringErrorKind;
    code?: string;
}

export type DashboardLoadCallback = (error?: MonitoringError) => void;
export type ProjectMutationCallback = (project: ProjectResponse | null, error?: MonitoringError) => void;
export type ProjectCertificateCheckCallback = (project: ProjectResponse | null, error?: MonitoringError) => void;
export type ServiceMutationCallback = (service: ServiceResponse | null, error?: MonitoringError) => void;
export type ServiceDeleteCallback = (error?: MonitoringError) => void;
export type CheckDetailCallback = (check: CheckResponse | null, error?: MonitoringError) => void;
export type CheckResultsCallback = (results: CheckResultListResponse | null, error?: MonitoringError) => void;
export type CheckLatestResultCallback = (result: CheckResultResponse | null, error?: MonitoringError) => void;
export type CheckRunCallback = (run: CheckRunResponse | null, error?: MonitoringError) => void;
export type CheckMutationCallback = (check: CheckResponse | null, error?: MonitoringError) => void;
export type CheckDeleteCallback = (error?: MonitoringError) => void;

export class MonitoringController {
    public static Initialized = false;

    private static dashboardRequestId = 0;
    private static dashboardRequestEvents: CheckExecutionRealtimeEvent[] = [];
    private static latestRealtimeEventByCheck = new Map<string, CheckExecutionRealtimeEvent>();
    private static latestRealtimeAggregateAtByProject = new Map<string, number>();

    public static dashboard: DashboardResponse | null = null;
    public static dashboardLoading = false;
    public static dashboardError: MonitoringError | null = null;
    public static selectedProjectId: string | null = null;

    // Reserved for the incident history and future dedicated service/check views.
    public static incidents: IncidentResponse[] = [];
    public static incidentsLoading = false;
    public static incidentsError: MonitoringError | null = null;

    public static projectMutationLoading = false;
    public static projectMutationError: MonitoringError | null = null;
    public static certificateCheckLoading = false;
    public static certificateCheckProjectId: string | null = null;
    public static certificateCheckError: MonitoringError | null = null;
    public static serviceMutationLoading = false;
    public static serviceMutationError: MonitoringError | null = null;
    public static checkDetail: CheckResponse | null = null;
    public static checkDetailLoading = false;
    public static checkDetailError: MonitoringError | null = null;
    public static checkResults: CheckResultResponse[] = [];
    public static checkResultsCheckId: string | null = null;
    public static checkResultsPage = 1;
    public static checkResultsStatus: CheckResultStatus | null = null;
    public static checkResultsTotalPages = 0;
    public static checkResultsTotal = 0;
    public static checkResultsLoading = false;
    public static checkResultsError: MonitoringError | null = null;
    public static latestCheckResult: CheckResultResponse | null = null;
    public static latestCheckResultCheckId: string | null = null;
    public static latestCheckResultLoading = false;
    public static latestCheckResultError: MonitoringError | null = null;
    public static checkRunLoading = false;
    public static checkRunCheckId: string | null = null;
    public static checkRunError: MonitoringError | null = null;
    public static checkMutationLoading = false;
    public static checkMutationError: MonitoringError | null = null;

    private static onAuthStatusChanged = () => {
        if (!AuthController.isAuthenticated()) {
            MonitoringController.clearState();
            return;
        }

        if (!MonitoringController.dashboard && !MonitoringController.dashboardLoading) {
            MonitoringController.LoadDashboard();
        }
    };

    private static onWebSocketAuthenticated = (reconnected: boolean) => {
        if (!reconnected || !AuthController.isAuthenticated()) {
            return;
        }

        MonitoringController.LoadDashboard();
        const check = MonitoringController.checkDetail;
        if (!check) {
            return;
        }

        MonitoringController.LoadCheck(check.id);
        MonitoringController.LoadLatestCheckResult(check.id);
        if (MonitoringController.checkResultsCheckId === check.id) {
            MonitoringController.LoadCheckResults(
                check.id,
                MonitoringController.checkResultsPage,
                10,
                MonitoringController.checkResultsStatus,
            );
        }
    };

    private static onMonitoringEvent = (message: MonitoringRealtimeEvent) => {
        if (message?.type !== "check.execution.completed") {
            return;
        }
        const event = message;
        if (
            !AuthController.isAuthenticated() ||
            !event ||
            event.event !== "monitoring-event" ||
            event.type !== "check.execution.completed" ||
            !event.checkId ||
            !event.resultId ||
            !Number.isFinite(event.observedAt) ||
            !Number.isFinite(event.check?.updatedAt) ||
            !Number.isFinite(event.result?.executedAt)
        ) {
            return;
        }

        const previous = MonitoringController.latestRealtimeEventByCheck.get(event.checkId);
        if (
            previous &&
            (previous.resultId === event.resultId ||
                event.check.updatedAt < previous.check.updatedAt ||
                (event.check.updatedAt === previous.check.updatedAt && event.observedAt <= previous.observedAt))
        ) {
            return;
        }

        MonitoringController.latestRealtimeEventByCheck.delete(event.checkId);
        MonitoringController.latestRealtimeEventByCheck.set(event.checkId, event);
        if (MonitoringController.latestRealtimeEventByCheck.size > 500) {
            const oldestCheckId = MonitoringController.latestRealtimeEventByCheck.keys().next().value;
            if (oldestCheckId) {
                MonitoringController.latestRealtimeEventByCheck.delete(oldestCheckId);
            }
        }
        if (MonitoringController.dashboardLoading) {
            MonitoringController.dashboardRequestEvents.push(event);
        }
        MonitoringController.applyRealtimeEventToDashboard(event);

        if (MonitoringController.checkDetail?.id === event.checkId) {
            if ((MonitoringController.checkDetail.lastExecutionAt || 0) <= event.check.lastExecutionAt) {
                MonitoringController.checkDetail = {
                    ...MonitoringController.checkDetail,
                    status: event.check.status,
                    lastExecutionAt: event.check.lastExecutionAt,
                    nextExecutionAt: event.check.nextExecutionAt,
                    updatedAt: event.check.updatedAt,
                };
            }

            MonitoringController.LoadLatestCheckResult(event.checkId);
            if (MonitoringController.checkResultsCheckId === event.checkId) {
                MonitoringController.LoadCheckResults(
                    event.checkId,
                    MonitoringController.checkResultsPage,
                    10,
                    MonitoringController.checkResultsStatus,
                );
            }
        }

        MonitoringController.emitChanged();
    };

    public static Initialize() {
        if (MonitoringController.Initialized) {
            return;
        }

        MonitoringController.Initialized = true;
        AppEvents.AddEventListener("auth-status-changed", MonitoringController.onAuthStatusChanged);
        AppEvents.AddEventListener("websocket-authenticated", MonitoringController.onWebSocketAuthenticated);
        AppEvents.AddEventListener("monitoring-event", MonitoringController.onMonitoringEvent);
        MonitoringController.onAuthStatusChanged();
    }

    public static GetDashboard(): DashboardResponse | null {
        return MonitoringController.dashboard;
    }

    public static GetProjects(): DashboardProjectResponse[] {
        return MonitoringController.dashboard?.projects.slice() || [];
    }

    public static GetProject(id: string): DashboardProjectResponse | null {
        return MonitoringController.dashboard?.projects.find((project) => project.id === id) || null;
    }

    public static GetServices(projectId: string) {
        return MonitoringController.GetProject(projectId)?.services.slice() || [];
    }

    public static GetService(id: string): DashboardServiceResponse | null {
        for (const project of MonitoringController.GetProjects()) {
            const service = project.services.find((item) => item.id === id);
            if (service) {
                return service;
            }
        }

        return null;
    }

    public static GetProjectForService(serviceId: string): DashboardProjectResponse | null {
        return MonitoringController.GetProjects().find((project) => project.services.some((service) => service.id === serviceId)) || null;
    }

    public static GetChecks(serviceId: string) {
        for (const project of MonitoringController.GetProjects()) {
            const service = project.services.find((item) => item.id === serviceId);
            if (service) {
                return service.checks.slice();
            }
        }

        return [];
    }

    public static GetCheck(id: string): CheckResponse | null {
        return MonitoringController.checkDetail?.id === id ? MonitoringController.checkDetail : null;
    }

    public static GetIncidents(): IncidentResponse[] {
        return MonitoringController.incidents.slice();
    }

    public static SelectProject(id: string | null) {
        MonitoringController.selectedProjectId = id;
    }

    public static LoadProjects(onDone?: DashboardLoadCallback) {
        MonitoringController.LoadDashboard(onDone);
    }

    public static LoadProject(id: string, onDone?: DashboardLoadCallback) {
        MonitoringController.SelectProject(id);
        MonitoringController.LoadDashboard(onDone);
    }

    public static LoadCheck(id: string, onDone?: CheckDetailCallback) {
        const execute = () => {
            Timeouts.Abort(CHECK_DETAIL_REQUEST_ID);
            Request.Abort(CHECK_DETAIL_REQUEST_ID);

            if (!AuthController.isAuthenticated()) {
                MonitoringController.completeCheckDetail(null, onDone, { kind: "unauthorized" });
                return;
            }

            if (MonitoringController.checkDetail?.id !== id) {
                MonitoringController.resetCheckHistory();
                MonitoringController.checkDetail = null;
            }

            MonitoringController.checkDetailLoading = true;
            MonitoringController.checkDetailError = null;
            MonitoringController.emitChanged();

            Request.Pending(CHECK_DETAIL_REQUEST_ID, ApiChecks.GetCheck(id))
                .onSuccess((check) => MonitoringController.completeCheckDetail(check, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        notFound: () => MonitoringController.completeCheckDetail(null, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeCheckDetail(null, onDone, { kind: "unauthorized" }),
                        serverError: () => MonitoringController.retryCheckDetail(execute, { kind: "server" }),
                        networkError: () => MonitoringController.retryCheckDetail(execute, { kind: "network" }),
                        temporalError: () => MonitoringController.retryCheckDetail(execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryCheckDetail(execute, { kind: "unexpected" });
                });
        };

        execute();
    }

    public static LoadCheckResults(
        id: string,
        page = 1,
        pageSize = 10,
        status: CheckResultStatus | null = null,
        onDone?: CheckResultsCallback,
    ) {
        const execute = () => {
            Timeouts.Abort(CHECK_RESULTS_REQUEST_ID);
            Request.Abort(CHECK_RESULTS_REQUEST_ID);

            if (!AuthController.isAuthenticated()) {
                MonitoringController.completeCheckResults(id, null, onDone, { kind: "unauthorized" });
                return;
            }

            if (MonitoringController.checkResultsCheckId !== id) {
                const latestResult = MonitoringController.latestCheckResultCheckId === id ? MonitoringController.latestCheckResult : null;
                MonitoringController.resetCheckHistory();
                MonitoringController.checkResultsCheckId = id;
                if (latestResult) {
                    MonitoringController.latestCheckResult = latestResult;
                    MonitoringController.latestCheckResultCheckId = id;
                }
            }
            MonitoringController.checkResultsPage = page;
            MonitoringController.checkResultsStatus = status;
            MonitoringController.checkResultsLoading = true;
            MonitoringController.checkResultsError = null;
            MonitoringController.emitChanged();

            Request.Pending(CHECK_RESULTS_REQUEST_ID, ApiChecks.ListCheckResults(id, { page, pageSize, status: status || undefined }))
                .onSuccess((results) => MonitoringController.completeCheckResults(id, results, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        badRequestInvalidPagination: () =>
                            MonitoringController.completeCheckResults(id, null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_PAGINATION",
                            }),
                        badRequestInvalidResultStatus: () =>
                            MonitoringController.completeCheckResults(id, null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_RESULT_STATUS",
                            }),
                        badRequest: () => MonitoringController.completeCheckResults(id, null, onDone, { kind: "bad-request" }),
                        notFound: () => MonitoringController.completeCheckResults(id, null, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeCheckResults(id, null, onDone, { kind: "unauthorized" }),
                        serverError: () => MonitoringController.retryCheckResults(execute, { kind: "server" }),
                        networkError: () => MonitoringController.retryCheckResults(execute, { kind: "network" }),
                        temporalError: () => MonitoringController.retryCheckResults(execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryCheckResults(execute, { kind: "unexpected" });
                });
        };

        execute();
    }

    public static LoadLatestCheckResult(id: string, onDone?: CheckLatestResultCallback) {
        const execute = () => {
            Timeouts.Abort(CHECK_LATEST_RESULT_REQUEST_ID);
            Request.Abort(CHECK_LATEST_RESULT_REQUEST_ID);

            if (!AuthController.isAuthenticated()) {
                MonitoringController.completeLatestCheckResult(id, null, onDone, { kind: "unauthorized" });
                return;
            }

            MonitoringController.latestCheckResultCheckId = id;
            MonitoringController.latestCheckResultLoading = true;
            MonitoringController.latestCheckResultError = null;
            MonitoringController.emitChanged();

            Request.Pending(CHECK_LATEST_RESULT_REQUEST_ID, ApiChecks.GetLatestCheckResult(id))
                .onSuccess((result) => MonitoringController.completeLatestCheckResult(id, result, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        notFound: () => MonitoringController.completeLatestCheckResult(id, null, onDone),
                        unauthorized: () => MonitoringController.completeLatestCheckResult(id, null, onDone, { kind: "unauthorized" }),
                        serverError: () => MonitoringController.retryLatestCheckResult(execute, { kind: "server" }),
                        networkError: () => MonitoringController.retryLatestCheckResult(execute, { kind: "network" }),
                        temporalError: () => MonitoringController.retryLatestCheckResult(execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryLatestCheckResult(execute, { kind: "unexpected" });
                });
        };

        execute();
    }

    public static RunCheck(id: string, onDone?: CheckRunCallback) {
        if (MonitoringController.checkRunLoading && MonitoringController.checkRunCheckId === id) {
            return;
        }

        const execute = () => {
            Timeouts.Abort(CHECK_RUN_REQUEST_ID);
            Request.Abort(CHECK_RUN_REQUEST_ID);

            if (!AuthController.isAuthenticated()) {
                MonitoringController.completeCheckRun(null, onDone, { kind: "unauthorized" });
                return;
            }

            MonitoringController.checkRunLoading = true;
            MonitoringController.checkRunCheckId = id;
            MonitoringController.checkRunError = null;
            MonitoringController.emitChanged();

            Request.Pending(CHECK_RUN_REQUEST_ID, ApiChecks.RunCheck(id))
                .onSuccess((run) => MonitoringController.completeCheckRun(run, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        notFound: () => MonitoringController.completeCheckRun(null, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeCheckRun(null, onDone, { kind: "unauthorized" }),
                        serverError: () => MonitoringController.retryCheckRun(execute, { kind: "server" }),
                        networkError: () => MonitoringController.retryCheckRun(execute, { kind: "network" }),
                        temporalError: () => MonitoringController.retryCheckRun(execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryCheckRun(execute, { kind: "unexpected" });
                });
        };

        execute();
    }

    public static CancelCheckInspectionRequests() {
        for (const requestId of [CHECK_DETAIL_REQUEST_ID, CHECK_RESULTS_REQUEST_ID, CHECK_LATEST_RESULT_REQUEST_ID, CHECK_RUN_REQUEST_ID]) {
            Timeouts.Abort(requestId);
            Request.Abort(requestId);
        }

        MonitoringController.checkDetailLoading = false;
        MonitoringController.checkResultsLoading = false;
        MonitoringController.latestCheckResultLoading = false;
        MonitoringController.checkRunLoading = false;
    }

    public static LoadDashboard(onDone?: DashboardLoadCallback) {
        const requestId = ++MonitoringController.dashboardRequestId;
        Timeouts.Abort(DASHBOARD_REQUEST_ID);
        Request.Abort(DASHBOARD_REQUEST_ID);

        if (!AuthController.isAuthenticated()) {
            const error: MonitoringError = { kind: "unauthorized" };
            MonitoringController.clearState(error);
            onDone?.(error);
            return;
        }

        MonitoringController.dashboardLoading = true;
        MonitoringController.dashboardError = null;
        const requestEvents: CheckExecutionRealtimeEvent[] = [];
        MonitoringController.dashboardRequestEvents = requestEvents;
        MonitoringController.emitChanged();

        Request.Pending(DASHBOARD_REQUEST_ID, ApiDashboard.GetDashboard())
            .onSuccess((dashboard) => {
                if (requestId !== MonitoringController.dashboardRequestId) {
                    return;
                }

                MonitoringController.dashboard = dashboard;
                MonitoringController.latestRealtimeAggregateAtByProject.clear();
                for (const event of requestEvents.sort((a, b) => a.observedAt - b.observedAt)) {
                    MonitoringController.applyRealtimeEventToDashboard(event);
                }

                MonitoringController.dashboardRequestEvents = [];
                MonitoringController.dashboardLoading = false;
                MonitoringController.dashboardError = null;
                MonitoringController.emitChanged();
                onDone?.();
            })
            .onRequestError((err, handleErr) => {
                if (requestId !== MonitoringController.dashboardRequestId) {
                    return;
                }

                handleErr(err, {
                    unauthorized: () => MonitoringController.completeDashboard({ kind: "unauthorized" }, onDone),
                    serverError: () => MonitoringController.retryDashboard({ kind: "server" }, onDone),
                    networkError: () => MonitoringController.retryDashboard({ kind: "network" }, onDone),
                    temporalError: () => MonitoringController.retryDashboard({ kind: "temporary" }, onDone),
                });
            })
            .onUnexpectedError((err) => {
                if (requestId !== MonitoringController.dashboardRequestId) {
                    return;
                }

                console.error(err);
                MonitoringController.retryDashboard({ kind: "unexpected" }, onDone);
            });
    }

    public static CreateProject(data: ProjectCreateRequest, onDone?: ProjectMutationCallback) {
        const execute = () => {
            Timeouts.Abort(PROJECT_CREATE_REQUEST_ID);
            Request.Abort(PROJECT_CREATE_REQUEST_ID);

            Request.Pending(PROJECT_CREATE_REQUEST_ID, ApiProjects.CreateProject(data))
                .onSuccess((project) => MonitoringController.completeProjectMutation(project, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        badRequestInvalidName: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_NAME" }),
                        badRequestInvalidDescription: () =>
                            MonitoringController.completeProjectMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_DESCRIPTION",
                            }),
                        badRequestInvalidColor: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_COLOR" }),
                        badRequestInvalidUrl: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_URL" }),
                        badRequestInvalidCertificateUrl: () =>
                            MonitoringController.completeProjectMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_CERTIFICATE_URL",
                            }),
                        badRequestInvalidBody: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_BODY" }),
                        badRequestInvalidFields: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_FIELDS" }),
                        badRequestNoFields: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "NO_FIELDS" }),
                        badRequestInvalidPagination: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_PAGINATION" }),
                        badRequest: () => MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request" }),
                        unauthorized: () => MonitoringController.completeProjectMutation(null, onDone, { kind: "unauthorized" }),
                        serverError: () =>
                            MonitoringController.retryProjectMutation(PROJECT_CREATE_REQUEST_ID, execute, { kind: "server" }),
                        networkError: () =>
                            MonitoringController.retryProjectMutation(PROJECT_CREATE_REQUEST_ID, execute, { kind: "network" }),
                        temporalError: () =>
                            MonitoringController.retryProjectMutation(PROJECT_CREATE_REQUEST_ID, execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryProjectMutation(PROJECT_CREATE_REQUEST_ID, execute, { kind: "unexpected" });
                });
        };

        MonitoringController.startProjectMutation();
        execute();
    }

    public static UpdateProject(id: string, data: ProjectUpdateRequest, onDone?: ProjectMutationCallback) {
        const execute = () => {
            Timeouts.Abort(PROJECT_UPDATE_REQUEST_ID);
            Request.Abort(PROJECT_UPDATE_REQUEST_ID);

            Request.Pending(PROJECT_UPDATE_REQUEST_ID, ApiProjects.UpdateProject(id, data))
                .onSuccess((project) => MonitoringController.completeProjectMutation(project, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        badRequestInvalidName: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_NAME" }),
                        badRequestInvalidDescription: () =>
                            MonitoringController.completeProjectMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_DESCRIPTION",
                            }),
                        badRequestInvalidColor: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_COLOR" }),
                        badRequestInvalidUrl: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_URL" }),
                        badRequestInvalidCertificateUrl: () =>
                            MonitoringController.completeProjectMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_CERTIFICATE_URL",
                            }),
                        badRequestInvalidBody: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_BODY" }),
                        badRequestInvalidFields: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_FIELDS" }),
                        badRequestNoFields: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "NO_FIELDS" }),
                        badRequestInvalidPagination: () =>
                            MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request", code: "INVALID_PAGINATION" }),
                        badRequest: () => MonitoringController.completeProjectMutation(null, onDone, { kind: "bad-request" }),
                        notFound: () => MonitoringController.completeProjectMutation(null, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeProjectMutation(null, onDone, { kind: "unauthorized" }),
                        serverError: () =>
                            MonitoringController.retryProjectMutation(PROJECT_UPDATE_REQUEST_ID, execute, { kind: "server" }),
                        networkError: () =>
                            MonitoringController.retryProjectMutation(PROJECT_UPDATE_REQUEST_ID, execute, { kind: "network" }),
                        temporalError: () =>
                            MonitoringController.retryProjectMutation(PROJECT_UPDATE_REQUEST_ID, execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryProjectMutation(PROJECT_UPDATE_REQUEST_ID, execute, { kind: "unexpected" });
                });
        };

        MonitoringController.startProjectMutation();
        execute();
    }

    public static RunProjectCertificateCheck(id: string, onDone?: ProjectCertificateCheckCallback) {
        if (MonitoringController.certificateCheckLoading && MonitoringController.certificateCheckProjectId === id) {
            return;
        }

        const execute = () => {
            Timeouts.Abort(PROJECT_CERTIFICATE_CHECK_REQUEST_ID);
            Request.Abort(PROJECT_CERTIFICATE_CHECK_REQUEST_ID);

            if (!AuthController.isAuthenticated()) {
                MonitoringController.completeProjectCertificateCheck(null, onDone, { kind: "unauthorized" });
                return;
            }

            MonitoringController.certificateCheckLoading = true;
            MonitoringController.certificateCheckProjectId = id;
            MonitoringController.certificateCheckError = null;
            MonitoringController.emitChanged();

            Request.Pending(PROJECT_CERTIFICATE_CHECK_REQUEST_ID, ApiProjects.RunProjectCertificateCheck(id))
                .onSuccess((project) => MonitoringController.completeProjectCertificateCheck(project, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        badRequestCertificateCheckDisabled: () =>
                            MonitoringController.completeProjectCertificateCheck(null, onDone, {
                                kind: "bad-request",
                                code: "CERTIFICATE_CHECK_DISABLED",
                            }),
                        badRequest: () => MonitoringController.completeProjectCertificateCheck(null, onDone, { kind: "bad-request" }),
                        notFound: () => MonitoringController.completeProjectCertificateCheck(null, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeProjectCertificateCheck(null, onDone, { kind: "unauthorized" }),
                        serverError: () => MonitoringController.retryProjectCertificateCheck(execute, { kind: "server" }),
                        networkError: () => MonitoringController.retryProjectCertificateCheck(execute, { kind: "network" }),
                        temporalError: () => MonitoringController.retryProjectCertificateCheck(execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryProjectCertificateCheck(execute, { kind: "unexpected" });
                });
        };

        execute();
    }

    public static CreateService(projectId: string, data: ServiceCreateRequest, onDone?: ServiceMutationCallback) {
        const execute = () => {
            Timeouts.Abort(SERVICE_CREATE_REQUEST_ID);
            Request.Abort(SERVICE_CREATE_REQUEST_ID);

            Request.Pending(SERVICE_CREATE_REQUEST_ID, ApiServices.CreateService(projectId, data))
                .onSuccess((service) => MonitoringController.completeServiceMutation(service, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        badRequestInvalidName: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "INVALID_NAME" }),
                        badRequestInvalidDescription: () =>
                            MonitoringController.completeServiceMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_DESCRIPTION",
                            }),
                        badRequestInvalidBody: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "INVALID_BODY" }),
                        badRequestInvalidFields: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "INVALID_FIELDS" }),
                        badRequestNoFields: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "NO_FIELDS" }),
                        badRequestInvalidPagination: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "INVALID_PAGINATION" }),
                        badRequest: () => MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request" }),
                        notFound: () => MonitoringController.completeServiceMutation(null, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeServiceMutation(null, onDone, { kind: "unauthorized" }),
                        serverError: () =>
                            MonitoringController.retryServiceMutation(SERVICE_CREATE_REQUEST_ID, execute, { kind: "server" }),
                        networkError: () =>
                            MonitoringController.retryServiceMutation(SERVICE_CREATE_REQUEST_ID, execute, { kind: "network" }),
                        temporalError: () =>
                            MonitoringController.retryServiceMutation(SERVICE_CREATE_REQUEST_ID, execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryServiceMutation(SERVICE_CREATE_REQUEST_ID, execute, { kind: "unexpected" });
                });
        };

        MonitoringController.startServiceMutation();
        execute();
    }

    public static UpdateService(id: string, data: ServiceUpdateRequest, onDone?: ServiceMutationCallback) {
        const execute = () => {
            Timeouts.Abort(SERVICE_UPDATE_REQUEST_ID);
            Request.Abort(SERVICE_UPDATE_REQUEST_ID);

            Request.Pending(SERVICE_UPDATE_REQUEST_ID, ApiServices.UpdateService(id, data))
                .onSuccess((service) => MonitoringController.completeServiceMutation(service, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        badRequestInvalidName: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "INVALID_NAME" }),
                        badRequestInvalidDescription: () =>
                            MonitoringController.completeServiceMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_DESCRIPTION",
                            }),
                        badRequestInvalidBody: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "INVALID_BODY" }),
                        badRequestInvalidFields: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "INVALID_FIELDS" }),
                        badRequestNoFields: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "NO_FIELDS" }),
                        badRequestInvalidPagination: () =>
                            MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request", code: "INVALID_PAGINATION" }),
                        badRequest: () => MonitoringController.completeServiceMutation(null, onDone, { kind: "bad-request" }),
                        notFound: () => MonitoringController.completeServiceMutation(null, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeServiceMutation(null, onDone, { kind: "unauthorized" }),
                        serverError: () =>
                            MonitoringController.retryServiceMutation(SERVICE_UPDATE_REQUEST_ID, execute, { kind: "server" }),
                        networkError: () =>
                            MonitoringController.retryServiceMutation(SERVICE_UPDATE_REQUEST_ID, execute, { kind: "network" }),
                        temporalError: () =>
                            MonitoringController.retryServiceMutation(SERVICE_UPDATE_REQUEST_ID, execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryServiceMutation(SERVICE_UPDATE_REQUEST_ID, execute, { kind: "unexpected" });
                });
        };

        MonitoringController.startServiceMutation();
        execute();
    }

    public static DeleteService(id: string, onDone?: ServiceDeleteCallback) {
        const execute = () => {
            Timeouts.Abort(SERVICE_DELETE_REQUEST_ID);
            Request.Abort(SERVICE_DELETE_REQUEST_ID);

            Request.Pending(SERVICE_DELETE_REQUEST_ID, ApiServices.DeleteService(id))
                .onSuccess(() => MonitoringController.completeServiceDeletion(id, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        notFound: () => MonitoringController.completeServiceDeletion(id, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeServiceDeletion(id, onDone, { kind: "unauthorized" }),
                        serverError: () =>
                            MonitoringController.retryServiceMutation(SERVICE_DELETE_REQUEST_ID, execute, { kind: "server" }),
                        networkError: () =>
                            MonitoringController.retryServiceMutation(SERVICE_DELETE_REQUEST_ID, execute, { kind: "network" }),
                        temporalError: () =>
                            MonitoringController.retryServiceMutation(SERVICE_DELETE_REQUEST_ID, execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryServiceMutation(SERVICE_DELETE_REQUEST_ID, execute, { kind: "unexpected" });
                });
        };

        MonitoringController.startServiceMutation();
        execute();
    }

    public static CreateCheck(serviceId: string, data: CheckCreateRequest, onDone?: CheckMutationCallback) {
        const execute = () => {
            Timeouts.Abort(CHECK_CREATE_REQUEST_ID);
            Request.Abort(CHECK_CREATE_REQUEST_ID);

            Request.Pending(CHECK_CREATE_REQUEST_ID, ApiChecks.CreateCheck(serviceId, data))
                .onSuccess((check) => MonitoringController.completeCheckMutation(check, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        badRequestInvalidRecoveryThreshold: () =>
                            MonitoringController.completeCheckMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_RECOVERY_THRESHOLD",
                            }),
                        badRequestInvalidFailureThreshold: () =>
                            MonitoringController.completeCheckMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_FAILURE_THRESHOLD",
                            }),
                        badRequestInvalidTimeout: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_TIMEOUT" }),
                        badRequestInvalidInterval: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_INTERVAL" }),
                        badRequestInvalidConfiguration: () =>
                            MonitoringController.completeCheckMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_CONFIGURATION",
                            }),
                        badRequestInvalidType: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_TYPE" }),
                        badRequestInvalidName: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_NAME" }),
                        badRequestInvalidBody: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_BODY" }),
                        badRequestInvalidFields: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_FIELDS" }),
                        badRequest: () => MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request" }),
                        notFound: () => MonitoringController.completeCheckMutation(null, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeCheckMutation(null, onDone, { kind: "unauthorized" }),
                        serverError: () => MonitoringController.retryCheckMutation(CHECK_CREATE_REQUEST_ID, execute, { kind: "server" }),
                        networkError: () => MonitoringController.retryCheckMutation(CHECK_CREATE_REQUEST_ID, execute, { kind: "network" }),
                        temporalError: () =>
                            MonitoringController.retryCheckMutation(CHECK_CREATE_REQUEST_ID, execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryCheckMutation(CHECK_CREATE_REQUEST_ID, execute, { kind: "unexpected" });
                });
        };

        MonitoringController.startCheckMutation();
        execute();
    }

    public static UpdateCheck(id: string, data: CheckUpdateRequest, onDone?: CheckMutationCallback) {
        const execute = () => {
            Timeouts.Abort(CHECK_UPDATE_REQUEST_ID);
            Request.Abort(CHECK_UPDATE_REQUEST_ID);

            Request.Pending(CHECK_UPDATE_REQUEST_ID, ApiChecks.UpdateCheck(id, data))
                .onSuccess((check) => MonitoringController.completeCheckMutation(check, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        badRequestNoFields: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "NO_FIELDS" }),
                        badRequestInvalidRecoveryThreshold: () =>
                            MonitoringController.completeCheckMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_RECOVERY_THRESHOLD",
                            }),
                        badRequestInvalidFailureThreshold: () =>
                            MonitoringController.completeCheckMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_FAILURE_THRESHOLD",
                            }),
                        badRequestInvalidTimeout: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_TIMEOUT" }),
                        badRequestInvalidInterval: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_INTERVAL" }),
                        badRequestInvalidConfiguration: () =>
                            MonitoringController.completeCheckMutation(null, onDone, {
                                kind: "bad-request",
                                code: "INVALID_CONFIGURATION",
                            }),
                        badRequestInvalidType: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_TYPE" }),
                        badRequestInvalidName: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_NAME" }),
                        badRequestInvalidBody: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_BODY" }),
                        badRequestInvalidFields: () =>
                            MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request", code: "INVALID_FIELDS" }),
                        badRequest: () => MonitoringController.completeCheckMutation(null, onDone, { kind: "bad-request" }),
                        notFound: () => MonitoringController.completeCheckMutation(null, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeCheckMutation(null, onDone, { kind: "unauthorized" }),
                        serverError: () => MonitoringController.retryCheckMutation(CHECK_UPDATE_REQUEST_ID, execute, { kind: "server" }),
                        networkError: () => MonitoringController.retryCheckMutation(CHECK_UPDATE_REQUEST_ID, execute, { kind: "network" }),
                        temporalError: () =>
                            MonitoringController.retryCheckMutation(CHECK_UPDATE_REQUEST_ID, execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryCheckMutation(CHECK_UPDATE_REQUEST_ID, execute, { kind: "unexpected" });
                });
        };

        MonitoringController.startCheckMutation();
        execute();
    }

    public static DeleteCheck(id: string, onDone?: CheckDeleteCallback) {
        const execute = () => {
            Timeouts.Abort(CHECK_DELETE_REQUEST_ID);
            Request.Abort(CHECK_DELETE_REQUEST_ID);

            Request.Pending(CHECK_DELETE_REQUEST_ID, ApiChecks.DeleteCheck(id))
                .onSuccess(() => MonitoringController.completeCheckDeletion(id, onDone))
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        notFound: () => MonitoringController.completeCheckDeletion(id, onDone, { kind: "not-found" }),
                        unauthorized: () => MonitoringController.completeCheckDeletion(id, onDone, { kind: "unauthorized" }),
                        serverError: () => MonitoringController.retryCheckMutation(CHECK_DELETE_REQUEST_ID, execute, { kind: "server" }),
                        networkError: () => MonitoringController.retryCheckMutation(CHECK_DELETE_REQUEST_ID, execute, { kind: "network" }),
                        temporalError: () =>
                            MonitoringController.retryCheckMutation(CHECK_DELETE_REQUEST_ID, execute, { kind: "temporary" }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    MonitoringController.retryCheckMutation(CHECK_DELETE_REQUEST_ID, execute, { kind: "unexpected" });
                });
        };

        MonitoringController.startCheckMutation();
        execute();
    }

    private static startProjectMutation() {
        MonitoringController.projectMutationLoading = true;
        MonitoringController.projectMutationError = null;
        MonitoringController.emitChanged();
    }

    private static retryProjectMutation(requestId: string, execute: () => void, error: MonitoringError) {
        MonitoringController.projectMutationError = error;
        MonitoringController.emitChanged();
        Timeouts.Set(requestId, 1500, execute);
    }

    private static completeProjectCertificateCheck(
        project: ProjectResponse | null,
        onDone: ProjectCertificateCheckCallback | undefined,
        error?: MonitoringError,
    ) {
        MonitoringController.certificateCheckLoading = false;
        MonitoringController.certificateCheckError = error || null;
        if (!error && project) {
            MonitoringController.mergeProject(project);
        }
        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }
        MonitoringController.emitChanged();
        onDone?.(project, error);
        if (!error && project) {
            MonitoringController.LoadDashboard();
        }
    }

    private static retryProjectCertificateCheck(execute: () => void, error: MonitoringError) {
        MonitoringController.certificateCheckLoading = false;
        MonitoringController.certificateCheckError = error;
        MonitoringController.emitChanged();
        Timeouts.Set(PROJECT_CERTIFICATE_CHECK_REQUEST_ID, 1500, execute);
    }

    private static startServiceMutation() {
        MonitoringController.serviceMutationLoading = true;
        MonitoringController.serviceMutationError = null;
        MonitoringController.emitChanged();
    }

    private static retryServiceMutation(requestId: string, execute: () => void, error: MonitoringError) {
        MonitoringController.serviceMutationError = error;
        MonitoringController.emitChanged();
        Timeouts.Set(requestId, 1500, execute);
    }

    private static startCheckMutation() {
        MonitoringController.checkMutationLoading = true;
        MonitoringController.checkMutationError = null;
        MonitoringController.emitChanged();
    }

    private static retryCheckMutation(requestId: string, execute: () => void, error: MonitoringError) {
        MonitoringController.checkMutationError = error;
        MonitoringController.emitChanged();
        Timeouts.Set(requestId, 1500, execute);
    }

    private static completeProjectMutation(
        project: ProjectResponse | null,
        onDone: ProjectMutationCallback | undefined,
        error?: MonitoringError,
    ) {
        MonitoringController.projectMutationLoading = false;
        MonitoringController.projectMutationError = error || null;

        if (!error && project) {
            MonitoringController.mergeProject(project);
        }

        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }

        MonitoringController.emitChanged();
        onDone?.(project, error);

        if (!error) {
            MonitoringController.LoadDashboard();
        }
    }

    private static mergeProject(project: ProjectResponse) {
        const current = MonitoringController.dashboard;
        const previous = current?.projects.find((item) => item.id === project.id);
        const merged: DashboardProjectResponse = {
            id: project.id,
            name: project.name,
            description: project.description,
            enabled: project.enabled,
            color: project.color,
            url: project.url,
            certificateCheck: project.certificateCheck,
            status: previous?.status || "UNKNOWN",
            services: previous?.services || [],
        };

        if (!current) {
            MonitoringController.dashboard = { generatedAt: Date.now(), projects: [merged] };
            return;
        }

        const exists = current.projects.some((item) => item.id === project.id);
        MonitoringController.dashboard = {
            ...current,
            projects: exists ? current.projects.map((item) => (item.id === project.id ? merged : item)) : [merged, ...current.projects],
        };
    }

    private static completeServiceMutation(
        service: ServiceResponse | null,
        onDone: ServiceMutationCallback | undefined,
        error?: MonitoringError,
    ) {
        MonitoringController.serviceMutationLoading = false;
        MonitoringController.serviceMutationError = error || null;

        if (!error && service) {
            MonitoringController.mergeService(service);
        }

        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }

        MonitoringController.emitChanged();
        onDone?.(service, error);

        if (!error) {
            MonitoringController.LoadDashboard();
        }
    }

    private static completeServiceDeletion(id: string, onDone: ServiceDeleteCallback | undefined, error?: MonitoringError) {
        MonitoringController.serviceMutationLoading = false;
        MonitoringController.serviceMutationError = error || null;

        if (!error) {
            MonitoringController.removeService(id);
        }

        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }

        MonitoringController.emitChanged();
        onDone?.(error);

        if (!error) {
            MonitoringController.LoadDashboard();
        }
    }

    private static completeCheckDetail(check: CheckResponse | null, onDone: CheckDetailCallback | undefined, error?: MonitoringError) {
        MonitoringController.checkDetailLoading = false;
        MonitoringController.checkDetailError = error || null;

        let visibleCheck = check;
        if (!error) {
            const current = MonitoringController.checkDetail;
            if (check && current?.id === check.id && (current.lastExecutionAt || 0) > (check.lastExecutionAt || 0)) {
                visibleCheck = current;
            } else {
                MonitoringController.checkDetail = check;
            }
        }

        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }

        MonitoringController.emitChanged();
        onDone?.(visibleCheck, error);
    }

    private static retryCheckDetail(execute: () => void, error: MonitoringError) {
        MonitoringController.checkDetailLoading = false;
        MonitoringController.checkDetailError = error;
        MonitoringController.emitChanged();
        Timeouts.Set(CHECK_DETAIL_REQUEST_ID, 1500, execute);
    }

    private static resetCheckHistory() {
        MonitoringController.checkResults = [];
        MonitoringController.checkResultsCheckId = null;
        MonitoringController.checkResultsPage = 1;
        MonitoringController.checkResultsStatus = null;
        MonitoringController.checkResultsTotalPages = 0;
        MonitoringController.checkResultsTotal = 0;
        MonitoringController.checkResultsLoading = false;
        MonitoringController.checkResultsError = null;
        MonitoringController.latestCheckResult = null;
        MonitoringController.latestCheckResultCheckId = null;
        MonitoringController.latestCheckResultLoading = false;
        MonitoringController.latestCheckResultError = null;
    }

    private static completeCheckResults(
        id: string,
        results: CheckResultListResponse | null,
        onDone: CheckResultsCallback | undefined,
        error?: MonitoringError,
    ) {
        MonitoringController.checkResultsLoading = false;
        MonitoringController.checkResultsError = error || null;

        if (!error && results) {
            MonitoringController.checkResultsCheckId = id;
            MonitoringController.checkResults = results.results;
            MonitoringController.checkResultsPage = results.page;
            MonitoringController.checkResultsTotalPages = results.totalPages;
            MonitoringController.checkResultsTotal = results.total;
        }

        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }

        MonitoringController.emitChanged();
        onDone?.(results, error);
    }

    private static retryCheckResults(execute: () => void, error: MonitoringError) {
        MonitoringController.checkResultsLoading = false;
        MonitoringController.checkResultsError = error;
        MonitoringController.emitChanged();
        Timeouts.Set(CHECK_RESULTS_REQUEST_ID, 1500, execute);
    }

    private static completeLatestCheckResult(
        id: string,
        result: CheckResultResponse | null,
        onDone: CheckLatestResultCallback | undefined,
        error?: MonitoringError,
    ) {
        MonitoringController.latestCheckResultLoading = false;
        MonitoringController.latestCheckResultError = error || null;

        if (!error) {
            MonitoringController.latestCheckResultCheckId = id;
            MonitoringController.latestCheckResult = result;

            if (
                result &&
                MonitoringController.checkResultsCheckId === id &&
                MonitoringController.checkResultsPage === 1 &&
                (!MonitoringController.checkResultsStatus || MonitoringController.checkResultsStatus === result.status)
            ) {
                const withoutResult = MonitoringController.checkResults.filter((item) => item.id !== result.id);
                MonitoringController.checkResults = [result, ...withoutResult];
            }
        }

        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }

        MonitoringController.emitChanged();
        onDone?.(result, error);
    }

    private static retryLatestCheckResult(execute: () => void, error: MonitoringError) {
        MonitoringController.latestCheckResultLoading = false;
        MonitoringController.latestCheckResultError = error;
        MonitoringController.emitChanged();
        Timeouts.Set(CHECK_LATEST_RESULT_REQUEST_ID, 1500, execute);
    }

    private static completeCheckRun(run: CheckRunResponse | null, onDone: CheckRunCallback | undefined, error?: MonitoringError) {
        MonitoringController.checkRunLoading = false;
        MonitoringController.checkRunError = error || null;

        const latestRealtimeEvent = run ? MonitoringController.latestRealtimeEventByCheck.get(run.check.id) : null;
        const newerRealtimeExecution =
            !!latestRealtimeEvent &&
            latestRealtimeEvent.resultId !== run?.result.id &&
            (latestRealtimeEvent.check.lastExecutionAt > (run?.check.lastExecutionAt || 0) ||
                (latestRealtimeEvent.check.lastExecutionAt === run?.check.lastExecutionAt &&
                    latestRealtimeEvent.check.updatedAt > (run?.check.updatedAt || 0)));

        if (!error && run && !newerRealtimeExecution) {
            MonitoringController.mergeCheck(run.check);
            MonitoringController.checkDetail = run.check;
            MonitoringController.latestCheckResult = run.result;
            MonitoringController.latestCheckResultCheckId = run.check.id;

            if (
                MonitoringController.checkResultsCheckId === run.check.id &&
                MonitoringController.checkResultsPage === 1 &&
                (!MonitoringController.checkResultsStatus || MonitoringController.checkResultsStatus === run.result.status)
            ) {
                const hadResult = MonitoringController.checkResults.some((item) => item.id === run.result.id);
                MonitoringController.checkResults = [
                    run.result,
                    ...MonitoringController.checkResults.filter((item) => item.id !== run.result.id),
                ];
                if (!hadResult) {
                    MonitoringController.checkResultsTotal += 1;
                }
            }
        }

        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }

        MonitoringController.emitChanged();
        onDone?.(run, error);

        if (!error && run && !newerRealtimeExecution) {
            MonitoringController.LoadCheckResults(run.check.id, 1, 10, MonitoringController.checkResultsStatus);
            MonitoringController.LoadDashboard();
        }
    }

    private static retryCheckRun(execute: () => void, error: MonitoringError) {
        MonitoringController.checkRunLoading = false;
        MonitoringController.checkRunError = error;
        MonitoringController.emitChanged();
        Timeouts.Set(CHECK_RUN_REQUEST_ID, 1500, execute);
    }

    private static completeCheckMutation(check: CheckResponse | null, onDone: CheckMutationCallback | undefined, error?: MonitoringError) {
        MonitoringController.checkMutationLoading = false;
        MonitoringController.checkMutationError = error || null;

        if (!error && check) {
            MonitoringController.mergeCheck(check);
            MonitoringController.checkDetail = check;
        }

        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }

        MonitoringController.emitChanged();
        onDone?.(check, error);

        if (!error) {
            MonitoringController.LoadDashboard();
        }
    }

    private static completeCheckDeletion(id: string, onDone: CheckDeleteCallback | undefined, error?: MonitoringError) {
        MonitoringController.checkMutationLoading = false;
        MonitoringController.checkMutationError = error || null;

        if (!error) {
            MonitoringController.removeCheck(id);
            if (MonitoringController.checkDetail?.id === id) {
                MonitoringController.checkDetail = null;
            }
        }

        if (error?.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }

        MonitoringController.emitChanged();
        onDone?.(error);

        if (!error) {
            MonitoringController.LoadDashboard();
        }
    }

    private static mergeService(service: ServiceResponse) {
        const current = MonitoringController.dashboard;
        if (!current) {
            return;
        }

        const projects = current.projects.map((project) => {
            if (project.id !== service.projectId) {
                return project;
            }

            const previous = project.services.find((item) => item.id === service.id);
            const merged: DashboardServiceResponse = {
                id: service.id,
                projectId: service.projectId,
                name: service.name,
                description: service.description,
                enabled: service.enabled,
                status: previous?.status || "UNKNOWN",
                checks: previous?.checks || [],
            };
            const exists = project.services.some((item) => item.id === service.id);

            return {
                ...project,
                services: exists ? project.services.map((item) => (item.id === service.id ? merged : item)) : [merged, ...project.services],
            };
        });

        MonitoringController.dashboard = { ...current, projects };
    }

    private static removeService(id: string) {
        const current = MonitoringController.dashboard;
        if (!current) {
            return;
        }

        MonitoringController.dashboard = {
            ...current,
            projects: current.projects.map((project) => ({
                ...project,
                services: project.services.filter((service) => service.id !== id),
            })),
        };
    }

    private static mergeCheck(check: CheckResponse) {
        const current = MonitoringController.dashboard;
        if (!current) {
            return;
        }

        MonitoringController.dashboard = {
            ...current,
            projects: current.projects.map((project) => ({
                ...project,
                services: project.services.map((service) => {
                    if (service.id !== check.serviceId) {
                        return service;
                    }

                    const previous = service.checks.find((item) => item.id === check.id);
                    const merged: DashboardCheckResponse = {
                        id: check.id,
                        serviceId: check.serviceId,
                        name: check.name,
                        type: check.type,
                        enabled: check.enabled,
                        status: check.status,
                        lastExecutionAt: check.lastExecutionAt,
                        nextExecutionAt: check.nextExecutionAt,
                        lastResult: previous?.lastResult,
                        openIncident: previous?.openIncident,
                    };
                    const exists = service.checks.some((item) => item.id === check.id);

                    return {
                        ...service,
                        checks: exists ? service.checks.map((item) => (item.id === check.id ? merged : item)) : [merged, ...service.checks],
                    };
                }),
            })),
        };
    }

    private static applyRealtimeEventToDashboard(event: CheckExecutionRealtimeEvent) {
        const current = MonitoringController.dashboard;
        if (!current || current.generatedAt >= event.observedAt) {
            return;
        }

        const project = current.projects.find((item) => item.id === event.projectId);
        const service = project?.services.find((item) => item.id === event.serviceId);
        const check = service?.checks.find((item) => item.id === event.checkId);
        if (!project || !service || !check || (check.lastExecutionAt || 0) > event.check.lastExecutionAt) {
            return;
        }

        const previousAggregateAt = MonitoringController.latestRealtimeAggregateAtByProject.get(event.projectId) || 0;
        const updateAggregate = event.observedAt >= previousAggregateAt;
        let openIncident = check.openIncident || null;
        if (event.incident?.action === "OPENED") {
            openIncident = {
                id: event.incident.id,
                checkId: event.incident.checkId,
                startedAt: event.incident.startedAt,
                resolvedAt: null,
                status: event.incident.status,
                duration: Math.max(0, event.observedAt - event.incident.startedAt),
            };
        } else if (event.incident?.action === "RESOLVED" && openIncident?.id === event.incident.id) {
            openIncident = null;
        }

        const lastResult = {
            id: event.result.id,
            checkId: event.checkId,
            status: event.result.status,
            responseTime: event.result.responseTime,
            httpStatusCode: event.result.httpStatusCode,
            executedAt: event.result.executedAt,
        };
        MonitoringController.dashboard = {
            ...current,
            projects: current.projects.map((projectItem) => {
                if (projectItem.id !== event.projectId) {
                    return projectItem;
                }

                return {
                    ...projectItem,
                    status: updateAggregate ? event.projectStatus : projectItem.status,
                    services: projectItem.services.map((serviceItem) => {
                        if (serviceItem.id !== event.serviceId) {
                            return serviceItem;
                        }

                        return {
                            ...serviceItem,
                            status: updateAggregate ? event.serviceStatus : serviceItem.status,
                            checks: serviceItem.checks.map((checkItem) =>
                                checkItem.id === event.checkId
                                    ? {
                                          ...checkItem,
                                          status: event.check.status,
                                          lastExecutionAt: event.check.lastExecutionAt,
                                          nextExecutionAt: event.check.nextExecutionAt,
                                          lastResult,
                                          openIncident,
                                      }
                                    : checkItem,
                            ),
                        };
                    }),
                };
            }),
        };

        if (updateAggregate) {
            MonitoringController.latestRealtimeAggregateAtByProject.set(event.projectId, event.observedAt);
        }
    }

    private static removeCheck(id: string) {
        const current = MonitoringController.dashboard;
        if (!current) {
            return;
        }

        MonitoringController.dashboard = {
            ...current,
            projects: current.projects.map((project) => ({
                ...project,
                services: project.services.map((service) => ({
                    ...service,
                    checks: service.checks.filter((check) => check.id !== id),
                })),
            })),
        };
    }

    private static completeDashboard(error: MonitoringError, onDone?: DashboardLoadCallback) {
        MonitoringController.dashboardLoading = false;
        MonitoringController.dashboardRequestEvents = [];
        MonitoringController.dashboardError = error;
        if (error.kind === "unauthorized") {
            AppEvents.Emit("unauthorized");
        }
        MonitoringController.emitChanged();
        onDone?.(error);
    }

    private static retryDashboard(error: MonitoringError, onDone?: DashboardLoadCallback) {
        MonitoringController.dashboardLoading = false;
        MonitoringController.dashboardRequestEvents = [];
        MonitoringController.dashboardError = error;
        MonitoringController.emitChanged();
        Timeouts.Set(DASHBOARD_REQUEST_ID, 1500, () => MonitoringController.LoadDashboard(onDone));
    }

    private static clearState(error: MonitoringError | null = null) {
        Timeouts.Abort(DASHBOARD_REQUEST_ID);
        Timeouts.Abort(PROJECT_CREATE_REQUEST_ID);
        Timeouts.Abort(PROJECT_UPDATE_REQUEST_ID);
        Timeouts.Abort(PROJECT_CERTIFICATE_CHECK_REQUEST_ID);
        Timeouts.Abort(SERVICE_CREATE_REQUEST_ID);
        Timeouts.Abort(SERVICE_UPDATE_REQUEST_ID);
        Timeouts.Abort(SERVICE_DELETE_REQUEST_ID);
        Timeouts.Abort(CHECK_DETAIL_REQUEST_ID);
        Timeouts.Abort(CHECK_RESULTS_REQUEST_ID);
        Timeouts.Abort(CHECK_LATEST_RESULT_REQUEST_ID);
        Timeouts.Abort(CHECK_RUN_REQUEST_ID);
        Timeouts.Abort(CHECK_CREATE_REQUEST_ID);
        Timeouts.Abort(CHECK_UPDATE_REQUEST_ID);
        Timeouts.Abort(CHECK_DELETE_REQUEST_ID);
        Request.Abort(DASHBOARD_REQUEST_ID);
        Request.Abort(PROJECT_CREATE_REQUEST_ID);
        Request.Abort(PROJECT_UPDATE_REQUEST_ID);
        Request.Abort(PROJECT_CERTIFICATE_CHECK_REQUEST_ID);
        Request.Abort(SERVICE_CREATE_REQUEST_ID);
        Request.Abort(SERVICE_UPDATE_REQUEST_ID);
        Request.Abort(SERVICE_DELETE_REQUEST_ID);
        Request.Abort(CHECK_DETAIL_REQUEST_ID);
        Request.Abort(CHECK_RESULTS_REQUEST_ID);
        Request.Abort(CHECK_LATEST_RESULT_REQUEST_ID);
        Request.Abort(CHECK_RUN_REQUEST_ID);
        Request.Abort(CHECK_CREATE_REQUEST_ID);
        Request.Abort(CHECK_UPDATE_REQUEST_ID);
        Request.Abort(CHECK_DELETE_REQUEST_ID);
        MonitoringController.dashboardRequestId++;
        MonitoringController.dashboardRequestEvents = [];
        MonitoringController.latestRealtimeEventByCheck.clear();
        MonitoringController.latestRealtimeAggregateAtByProject.clear();
        MonitoringController.dashboard = null;
        MonitoringController.dashboardLoading = false;
        MonitoringController.dashboardError = error;
        MonitoringController.selectedProjectId = null;
        MonitoringController.incidents = [];
        MonitoringController.incidentsLoading = false;
        MonitoringController.incidentsError = null;
        MonitoringController.projectMutationLoading = false;
        MonitoringController.projectMutationError = null;
        MonitoringController.certificateCheckLoading = false;
        MonitoringController.certificateCheckProjectId = null;
        MonitoringController.certificateCheckError = null;
        MonitoringController.serviceMutationLoading = false;
        MonitoringController.serviceMutationError = null;
        MonitoringController.checkDetail = null;
        MonitoringController.checkDetailLoading = false;
        MonitoringController.checkDetailError = null;
        MonitoringController.resetCheckHistory();
        MonitoringController.checkRunLoading = false;
        MonitoringController.checkRunCheckId = null;
        MonitoringController.checkRunError = null;
        MonitoringController.checkMutationLoading = false;
        MonitoringController.checkMutationError = null;
        MonitoringController.emitChanged();
    }

    private static emitChanged() {
        AppEvents.Emit("monitoring-changed");
    }
}
